import React, { useLayoutEffect, useMemo, useState } from 'react'
import PageLayout from '../../../components/layout/PageLayout'
import onImagePasted from '../../../hooks/etc/onImagePasted'
import WizButton from '../../../components/button/WizButton'
import AnnouncementMenuView from '../../../features/page/post/announcement/AnnouncementMenuView'
import AnnouncementInputView from '../../../features/page/post/announcement/AnnouncementInputView'
import AnnouncementMarkdownView from '../../../features/page/post/announcement/AnnouncementMarkdownView'
import Text from '../../../components/text/Text'
import AnnouncementPreview from '../../../features/page/post/announcement/AnnouncementPreview'
import { useNavigate, useSearchParams } from 'react-router-dom'
import useQueryAnnounceDetail from '../../../hooks/api/announce/useQueryAnnounceDetail'
import { AnnounceContensType, AnnouncementReduceState, useAnnouncementReduce } from './AnnounceCreate'
import { postEditAnnounce } from '../../../apis/announce/editAnnounce'
import WizSpinner from '../../../components/spinner/WizSpinner'
import useQueryUserDetail from '../../../hooks/api/user/useQueryUserDetail'
import WizImage from '../../../components/image/WizImage'
import { useTime } from '../../../utils/time'

export const DEFAULT_TITLE_EXECUTE_STRING = '># '

function AnnounceDetailPage() {
    const [searchParams] = useSearchParams(document.location.search)
    const announceId = searchParams.get('id')
    const { data, refresh } = useQueryAnnounceDetail(announceId)
    const navigation = useNavigate()
    const { data: userData, refetch } = useQueryUserDetail(localStorage?.getItem('wiz-admin-userId'))
    const { convertInputToTimestamp } = useTime()

    const [isLoading, setIsLoading] = useState(false)

    const initState: AnnouncementReduceState = {
        value: `${DEFAULT_TITLE_EXECUTE_STRING}${data?.title}` + '\n' + data?.content,
        categories: data?.categories,
        channel: data?.channel,
        visibility: data?.visibility,
        thumbnail: data?.thumbnail,
        important_at: data?.important_at,
        pin_priority: data?.pin_priority,
        upload_at: data?.upload_at ? convertInputToTimestamp(data.upload_at) : undefined,
        created_at: data?.created_at,
        notify: data?.notify,
        notify_expires_at: data?.notify_expires_at,
    }

    const { state, reset, setValue, setCategories, setImages, setChannel, setVisibility, setThumbnail, setImportantAt, setPinPriority, setUploadAt, setNotify, setNotifyExpiresAt } =
        useAnnouncementReduce(initState)

    const {
        value,
        categories,
        images,
        channel,
        visibility,
        thumbnail,
        important_at,
        pin_priority,
        upload_at,
        created_at,
        notify,
        notify_expires_at,
    }: AnnouncementReduceState = state
    const firstImage = useMemo(() => {
        if (!value) return ''
        let splits = value?.split('\n')
        const imageArray = splits
            .map(e => e?.match(/!\[[^\]]*\]\((.*?)\s*("(?:.*[^"])")?\s*\)/g)?.shift())
            .map(e => e?.match(/\b(https?:\/\/.*?\.[a-z]{2,4}\/[^\s]*\b)/g)?.shift())
            .filter(element => element)
        if (imageArray && imageArray.length > 0) {
            return imageArray[0].replace('?action=fit', '')
        }
        return ''
    }, [value])

    useLayoutEffect(() => reset(), [data])

    const announceContents: AnnounceContensType = useMemo(() => {
        if (!value) return { title: '', content: '' }
        let splits = value?.split('\n')
        let title: string
        let content: string

        const firstLine = splits[0]
        const hasTitle = firstLine.indexOf(DEFAULT_TITLE_EXECUTE_STRING)
        if (hasTitle !== -1) {
            title = firstLine.replace(DEFAULT_TITLE_EXECUTE_STRING, '')
            content = value?.replace(firstLine, '').slice(1)
        } else {
            content = value
        }
        return { title: title, content: content }
    }, [value])

    const handleMarkdownImage = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        const newImage = await onImagePasted(event.dataTransfer, setValue)
        setImages([...images, ...newImage])
    }

    const onClickEditPost = async () => {
        const { data, error } = await postEditAnnounce({
            id: announceId,
            type: 'announce',
            title: announceContents.title,
            content: announceContents.content,
            channel: channel,
            visibility: visibility,
            categories: categories,
            thumbnail: thumbnail || firstImage,
            created_at: created_at,
            important_at: important_at,
            pin_priority: pin_priority,
            upload_at: upload_at ? Math.floor(upload_at) : upload_at,
            notify: notify,
            notify_expires_at: notify_expires_at,
        })
        if (data) {
            setIsLoading(false)
            alert('공지 글 수정에 성공했습니다.')
            navigation(`/announce`)
            return
        }
        if (error) {
            setIsLoading(false)
            return alert('공지 글 수정에 실패했습니다.')
        }
    }

    const onClickAddTag = (category: string) => {
        switch (category) {
            case 'Official':
                category = 'announcement'
                break
            case 'Event':
                category = 'event'
                break
            case 'Media':
                category = 'media'
                break
        }
        const list = Array.from(new Set([...categories, category]))
        setCategories(list)
    }
    const onClickRemoveCategory = (category: string) => {
        categories.splice(categories.indexOf(category), 1)
        setCategories([...categories])
    }
    const onClickRemoveImportantAt = () => {
        setImportantAt(created_at)
    }

    const onAddThumbnail = (thumbnail?: string) => {
        const newThunmbnail = thumbnail.replace('?action=fit', '')
        setThumbnail(newThunmbnail)
    }

    const isNotMatchUser = useMemo(() => {
        return data?.user_id !== userData?.id
    }, [data, userData])

    return (
        <PageLayout title={'Announcement Detail'}>
            <div data-color-mode="light">
                <AnnouncementMenuView
                    channel={channel}
                    setChannel={setChannel}
                    visibility={visibility}
                    setVisibility={setVisibility}
                    importantAt={important_at}
                    setImportantAt={setImportantAt}
                    pinPriority={pin_priority}
                    setPinPriority={setPinPriority}
                    uploadAt={upload_at}
                    setUploadAt={setUploadAt}
                    thumbnail={thumbnail}
                    setThumbnail={onAddThumbnail}
                    notify={notify}
                    setNotify={setNotify}
                    notifyExpiresAt={notify_expires_at}
                    setNotifyExpiresAt={setNotifyExpiresAt}
                    onClickAddTag={onClickAddTag}>
                    <div className={'mb-[30px] '}>
                        <div className={'flex gap-x-[10px]'}>
                            <div>
                                <Text className={'text-ti3'}>Now Login Account</Text>
                                <div
                                    className={`flex items-center gap-x-[5px] mt-[7px] px-[30px] py-[10px] border rounded-[5px] text-center w-fit ${isNotMatchUser ? 'border-font_red bg-bg_red' : 'border-line02 bg-white'
                                        }`}>
                                    <WizImage
                                        src={`${userData?.thumbnail}?type=webp&width=1000&quality=100`}
                                        className={'w-[36px] h-[36px] rounded-full'}
                                    />
                                    <Text className={`text-ti2 ${isNotMatchUser && 'text-font_red'}`}>{userData?.name}</Text>
                                </div>
                            </div>
                            <div>
                                <Text className={'text-ti3'}>Post Write User</Text>
                                <div
                                    className={
                                        'flex items-center gap-x-[5px] bg-white mt-[7px] px-[30px] py-[10px] border border-line02 rounded-[5px] text-center w-fit'
                                    }>
                                    <WizImage
                                        src={`${data?.user?.thumbnail}?type=webp&width=1000&quality=100`}
                                        className={'w-[36px] h-[36px] rounded-full'}
                                    />
                                    <Text className={'text-ti2'}>{data?.user?.name}</Text>
                                </div>
                            </div>
                        </div>
                        {isNotMatchUser && <Text className={'text-font_red text-body3'}>Writer and Login User Not Match!</Text>}
                    </div>
                </AnnouncementMenuView>

                <div className={'grid grid-cols-2 gap-[20px] min-h-full md:grid-cols-1 md:mt-[30px]'}>
                    <AnnouncementInputView
                        value={value}
                        setValue={setValue}
                        handleMarkdownImage={handleMarkdownImage}
                    />
                    <div className={'space-y-[10px]'}>
                        <Text className={'text-h3'}>Preview</Text>
                        <AnnouncementPreview
                            categories={categories}
                            announceContents={announceContents}
                            importantAt={important_at}
                            thumbnail={thumbnail || firstImage}
                            onClickRemoveCategory={onClickRemoveCategory}
                            onClickRemoveImportantAt={onClickRemoveImportantAt}
                        />
                        <Text className={'text-h3'}>Detail</Text>
                        <AnnouncementMarkdownView
                            categories={categories}
                            announceContents={announceContents}
                            importantAt={important_at}
                            onClickRemoveCategory={onClickRemoveCategory}
                            onClickRemoveImportantAt={onClickRemoveImportantAt}
                        />
                    </div>
                </div>
            </div>
            <div className={'text-center mt-[20px]'}>
                {!isLoading ? (
                    <WizButton
                        text={'Edit Announcement'}
                        size={'medium'}
                        onClick={() => {
                            setIsLoading(true)
                            onClickEditPost()
                        }}
                    />
                ) : (
                    <WizSpinner />
                )}
            </div>
        </PageLayout>
    )
}

export default AnnounceDetailPage
